import { useEffect, useState } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { AdminSection } from "./AdminSection";

interface BillingSummary {
	generatedAtMs: number;
	periodStartMs: number;
	googleRoutes: {
		requests: number;
		successfulRequests: number;
		failedRequests: number;
		trackedFromMs: number | null;
		freeRequestsRemaining: number;
		estimatedCostUsd: number;
	};
	cloudflare: {
		workersMinimumUsd: number;
		d1StorageBytes: number;
		d1IncludedBytes: number;
		r2StorageBytes: number;
		r2IncludedBytes: number;
		r2Objects: number;
	};
}

function bytes(value: number): string {
	if (value < 1_000_000) return `${Math.round(value / 1_000)} KB`;
	if (value < 1_000_000_000) return `${(value / 1_000_000).toFixed(1)} MB`;
	return `${(value / 1_000_000_000).toFixed(2)} GB`;
}

function money(value: number): string {
	return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

async function responseMessage(response: Response): Promise<string> {
	const body = (await response.json().catch(() => null)) as { error?: { message?: string } } | null;
	return body?.error?.message ?? `Request failed (${response.status}).`;
}

export function BillingPanel() {
	const [summary, setSummary] = useState<BillingSummary | null>(null);
	const [status, setStatus] = useState<string | null>(null);
	const [busy, setBusy] = useState(false);

	const load = async () => {
		setBusy(true);
		setStatus(null);
		try {
			const response = await fetch("/api/admin/billing");
			if (!response.ok) throw new Error(await responseMessage(response));
			setSummary(await response.json() as BillingSummary);
		} catch (cause) {
			setStatus(cause instanceof Error ? cause.message : "Could not load billing data.");
		} finally {
			setBusy(false);
		}
	};

	useEffect(() => { void load(); }, []);

	return <AdminSection title="Cost watch" className="admin-billing">
		<div className="admin-section__head">
			<div><p>Current-month activity and charge exposure</p></div>
			<button className="admin-add" disabled={busy} onClick={() => void load()}><RefreshCw size={14} /> {busy ? "Checking…" : "Refresh"}</button>
		</div>
		{status && <p className="admin-status" role="status">{status}</p>}
		{summary && <>
			<div className="admin-billing__grid">
				<article>
					<span>Cloudflare</span><strong>{money(summary.cloudflare.workersMinimumUsd)} minimum</strong>
					<p>Workers Paid monthly baseline. Request, CPU, D1 operation, Workers AI, and R2 operation overages are visible only in Cloudflare billing.</p>
				</article>
				<article>
					<span>Google Routes Pro</span><strong>{money(summary.googleRoutes.estimatedCostUsd)} estimated</strong>
					<p>{summary.googleRoutes.requests.toLocaleString()} observed requests · {summary.googleRoutes.freeRequestsRemaining.toLocaleString()} of the 5,000 free monthly calls remain.</p>
				</article>
				<article>
					<span>D1 storage snapshot</span><strong>{bytes(summary.cloudflare.d1StorageBytes)}</strong>
					<p>{bytes(summary.cloudflare.d1IncludedBytes)} included monthly. Row reads and writes are not available from inside D1.</p>
				</article>
				<article>
					<span>R2 speech snapshot</span><strong>{bytes(summary.cloudflare.r2StorageBytes)}</strong>
					<p>{summary.cloudflare.r2Objects.toLocaleString()} objects · {bytes(summary.cloudflare.r2IncludedBytes)} included monthly.</p>
				</article>
			</div>
			<div className="admin-billing__notes">
				<p><strong>Coverage:</strong> Google counts start with this release{summary.googleRoutes.trackedFromMs ? `; this month is tracked from ${new Date(summary.googleRoutes.trackedFromMs).toLocaleString()}` : "; no calls have been observed yet"}. The estimate uses published list prices and includes {summary.googleRoutes.failedRequests.toLocaleString()} non-2xx response(s) conservatively. It excludes other projects, credits, discounts, and taxes.</p>
				<p><strong>Not an invoice:</strong> Cloudflare does not make account billing available to this Worker. Storage values are live snapshots, not monthly averages.</p>
			</div>
			<div className="admin-billing__links">
				<a href="https://dash.cloudflare.com/?to=/:account/billing" target="_blank" rel="noreferrer">Cloudflare billing <ExternalLink size={13} /></a>
				<a href="https://console.cloud.google.com/billing" target="_blank" rel="noreferrer">Google Cloud billing <ExternalLink size={13} /></a>
				<a href="https://console.cloud.google.com/google/maps-apis/quotas" target="_blank" rel="noreferrer">Google quotas <ExternalLink size={13} /></a>
			</div>
		</>}
	</AdminSection>;
}